import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import { VStack } from "@chakra-ui/react";
import { FiAlertTriangle, FiDownload } from "react-icons/fi";

import Modal from "newui/components/ui/Modal/Modal";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { DateInputWithLabel } from "newui/components/ui/InputWithLabel/DateInputWithLabel";
import { FileUpload } from "components/FormInputs/FileUpload";
import { Currency } from "components/FormInputs/Currency";
import { updateDocument } from "newui/features/Company/company-api";
import { companyDocumentKeys } from "../companyDocumentKeys";
import { ChangeHistory } from "./ChangeHistory";
import { showToast } from "utils/helpers";
import type { CorporateDocument } from "newui/features/Company/types";

const INSURANCE_CATEGORY_ID = 4;

interface InsuranceFields {
	expiry_date?: string | null;
	insured_name?: string | null;
	insurer_name?: string | null;
	override_reasons?: Record<string, string>;
	policy_number?: string | null;
	textract_inferred_sub_category?: string | null;
	value?: string | number | null;
}

interface EditDocumentFormValues {
	name: string;
	extra_info: {
		[key: string]: unknown;
		expiry_date: string;
		value: string;
	};
	file: { name: string; storagePath: string | null } | File | string;
	override_reasons: {
		expiry_date: string;
		value: string;
	};
}

interface EditDocumentModalProps {
	companyId: number;
	document: CorporateDocument;
	isOpen: boolean;
	onClose: () => void;
}

const getInsuranceFields = (extraInfo: Record<string, unknown>): InsuranceFields | null => {
	const insurance = extraInfo?.insurance;
	if (!insurance || typeof insurance !== "object") {
		return null;
	}
	return insurance as InsuranceFields;
};

const ReadOnlyField: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => {
	if (!value) {
		return null;
	}
	return (
		<div>
			<p className="text-xs font-medium text-gray-500">{label}</p>
			<p className="text-sm text-gray-900">{value}</p>
		</div>
	);
};

const normaliseValue = (val: string | number | null | undefined): string => {
	if (val == null || val === "") {
		return "";
	}
	return String(Number(val) || val);
};

const buildFormData = (
	values: EditDocumentFormValues,
	insurance: InsuranceFields | null,
): FormData => {
	const formData = new FormData();
	formData.append("name", values.name);

	if (values.file instanceof File) {
		formData.append("file", values.file);
	}

	if (values.extra_info) {
		Object.entries(values.extra_info).forEach(([key, val]) => {
			if (val !== "" && val !== null && val !== undefined) {
				formData.append(`extra_info[${key}]`, String(val));
			}
		});
	}

	// Persist override reasons if values differ from verified
	if (insurance) {
		const reasons: Record<string, string> = {};
		const verifiedExpiry = insurance.expiry_date ?? "";
		const verifiedValue = normaliseValue(insurance.value);

		if (values.extra_info.expiry_date && values.extra_info.expiry_date !== verifiedExpiry && values.override_reasons.expiry_date) {
			reasons.expiry_date = values.override_reasons.expiry_date;
		}
		if (normaliseValue(values.extra_info.value) !== verifiedValue && values.override_reasons.value) {
			reasons.value = values.override_reasons.value;
		}

		if (Object.keys(reasons).length > 0) {
			Object.entries(reasons).forEach(([key, val]) => {
				formData.append(`extra_info[override_reasons][${key}]`, val);
			});
		}
	}

	return formData;
};

export const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
	companyId,
	document,
	isOpen,
	onClose,
}) => {
	const queryClient = useQueryClient();
	const isInsurance =
		document.category?.parent_id === INSURANCE_CATEGORY_ID ||
		document.category?.id === INSURANCE_CATEGORY_ID;

	const insurance = isInsurance ? getInsuranceFields(document.extra_info ?? {}) : null;

	// Override reasons live at top-level extra_info (written by edit save),
	// falling back to insurance sub-key (written by confirm flow).
	const existingReasons = (document.extra_info?.override_reasons as Record<string, string> | undefined)
		?? insurance?.override_reasons
		?? {};

	// Current values come from top-level extra_info (reflects latest edit/confirm).
	// insurance.* is the OCR-verified reference only — used for diff warnings.
	const expiryDate = document.extra_info?.expiry_date || insurance?.expiry_date || "";
	const value = document.extra_info?.value || insurance?.value || "";

	const initialValues: EditDocumentFormValues = {
		name: document.name || "",
		file: {
			name: document.file_name || document.name || "",
			storagePath: document.storage_path,
		},
		extra_info: {
			expiry_date: String(expiryDate),
			value: String(value),
		},
		override_reasons: {
			expiry_date: existingReasons.expiry_date || "",
			value: existingReasons.value || "",
		},
	};

	const validationSchema = Yup.object().shape({
		name: Yup.string().trim().required("Document name is required"),
	});

	const mutation = useMutation({
		mutationFn: (formData: FormData) =>
			updateDocument(companyId, document.id, formData),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: companyDocumentKeys.documents(companyId),
			});
			showToast("Document updated successfully.", "Success", true);
			onClose();
		},
		onError: () => {
			showToast("Failed to update document. Please try again.", "Error");
		},
	});

	const handleSubmit = (
		values: EditDocumentFormValues,
		_helpers: FormikHelpers<EditDocumentFormValues>
	) => {
		// Validate override reasons are provided when values differ
		if (insurance) {
			const verifiedExpiry = insurance.expiry_date ?? "";
			const verifiedValue = normaliseValue(insurance.value);

			if (values.extra_info.expiry_date !== verifiedExpiry && !values.override_reasons.expiry_date.trim()) {
				showToast("Please provide a reason for changing the expiry date.", "Error");
				return;
			}
			if (normaliseValue(values.extra_info.value) !== verifiedValue && !values.override_reasons.value.trim()) {
				showToast("Please provide a reason for changing the value.", "Error");
				return;
			}
		}

		const formData = buildFormData(values, insurance);
		mutation.mutate(formData);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Edit Document"
			iconType="edit"
			size="xl"
		>
			<Formik<EditDocumentFormValues>
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ setFieldValue, values, isSubmitting }) => {
					const expiryChanged = insurance != null && values.extra_info.expiry_date !== (insurance.expiry_date ?? "");
					const valueChanged = insurance != null && normaliseValue(values.extra_info.value) !== normaliseValue(insurance.value);

					return (
						<Form>
							<VStack spacing={4} align="stretch">
								<ChangeHistory companyId={companyId} documentId={document.id} />
								{document.storage_path && (
									<div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
										<FiDownload size={16} className="shrink-0 text-blue-600" />
										<a
											href={document.storage_path}
											target="_blank"
											rel="noopener noreferrer"
											className="text-sm font-medium text-blue-600 hover:underline"
										>
											View Document
										</a>
									</div>
								)}

								<FormikInputWithLabel
									type="text"
									name="name"
									label="Document Name"
									required
									testId="edit-document-name"
								/>

								{isInsurance && insurance && (
									<div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
										<p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
											Verified Insurance Details
										</p>
										<div className="grid grid-cols-2 gap-3">
											<ReadOnlyField label="Insurer" value={insurance.insurer_name} />
											<ReadOnlyField label="Insured" value={insurance.insured_name} />
											<ReadOnlyField label="Policy Number" value={insurance.policy_number} />
											<ReadOnlyField label="Insurance Type" value={insurance.textract_inferred_sub_category} />
											<ReadOnlyField
												label="Expiry Date (verified)"
												value={insurance.expiry_date}
											/>
											<ReadOnlyField
												label="Value (verified)"
												value={insurance.value != null ? `$${Number(insurance.value).toLocaleString()}` : null}
											/>
										</div>
										{existingReasons && Object.keys(existingReasons).length > 0 && (
											<div className="mt-2 border-t border-gray-200 pt-2">
												<p className="text-xs font-medium text-gray-500 mb-1">Previous Override Reasons</p>
												{Object.entries(existingReasons).map(([key, reason]) => (
													<p key={key} className="text-xs text-amber-700">
														<span className="font-medium capitalize">{key.replace(/_/g, " ")}:</span> {reason}
													</p>
												))}
											</div>
										)}
									</div>
								)}

								{isInsurance && (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<DateInputWithLabel
												name="extra_info[expiry_date]"
												label="Expiry Date"
												value={
													values.extra_info?.expiry_date
														? new Date(values.extra_info.expiry_date + "T00:00:00")
														: null
												}
												onChange={(date: Date | null) => {
													if (date) {
														const yyyy = date.getFullYear();
														const mm = String(date.getMonth() + 1).padStart(2, "0");
														const dd = String(date.getDate()).padStart(2, "0");
														setFieldValue(
															"extra_info.expiry_date",
															`${yyyy}-${mm}-${dd}`
														);
													}
												}}
												required={isInsurance}
											/>
											{expiryChanged && (
												<div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 mt-2">
													<FiAlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
													<div className="flex-1">
														<p className="text-xs font-medium text-amber-800 mb-1">
															Expiry date differs from verified value ({insurance?.expiry_date}). Please provide a reason:
														</p>
														<Field
															name="override_reasons.expiry_date"
															type="text"
															placeholder="Reason for change..."
															className="w-full rounded-md border border-amber-300 bg-white px-2 py-1.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
														/>
													</div>
												</div>
											)}
										</div>

										<div>
											<Currency
												label="Value"
												required={isInsurance}
												name="extra_info[value]"
												type="number"
												lang="en_EN"
												setFieldValue={setFieldValue}
												values={values}
											/>
											{valueChanged && (
												<div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 mt-2">
													<FiAlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
													<div className="flex-1">
														<p className="text-xs font-medium text-amber-800 mb-1">
															Value differs from verified amount (${insurance?.value != null ? Number(insurance.value).toLocaleString() : "N/A"}). Please provide a reason:
														</p>
														<Field
															name="override_reasons.value"
															type="text"
															placeholder="Reason for change..."
															className="w-full rounded-md border border-amber-300 bg-white px-2 py-1.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
														/>
													</div>
												</div>
											)}
										</div>
									</div>
								)}

								<FileUpload
									label="Replace File (optional)"
									required=""
									multi={false}
									values={values}
									name="file"
									setFieldValue={setFieldValue}
								/>

								<div className="flex justify-end gap-3 pt-2">
									<button
										type="button"
										onClick={onClose}
										className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
									>
										Cancel
									</button>
									<button
										type="submit"
										disabled={isSubmitting || mutation.isPending}
										className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
									>
										{mutation.isPending ? "Saving..." : "Save Changes"}
									</button>
								</div>
							</VStack>
						</Form>
					);
				}}
			</Formik>
		</Modal>
	);
};
